/**
 * HR board (`GET /api/tickets/board`, DESIGN.md §3.2): the queue filters minus status, grouped
 * into one column per status. Each column carries at most `BOARD_COLUMN_LIMIT` tickets plus its
 * full count, so the UI can say "Showing 100 of 340" instead of silently dropping cards.
 */
import { z } from 'zod'
import type { Status } from '../config.ts'
import { personRefSchema } from './api.ts'
import { queueTicketSchema } from './queue.ts'

/** Cards per column the server sends; the column's `total` says how many match. */
export const BOARD_COLUMN_LIMIT = 100

export const boardColumnSchema = z.object({
  statusId: z.string(),
  /** Tickets in this status matching the filters (may exceed `tickets.length`). */
  total: z.number().int(),
  tickets: z.array(queueTicketSchema),
})
export type BoardColumnData = z.infer<typeof boardColumnSchema>

/** `GET /api/tickets/board`: every status with at least one matching ticket. */
export const boardResponseSchema = z.object({
  columns: z.array(boardColumnSchema),
  /** Tickets matching the filters (all columns). */
  total: z.number().int(),
  /** Every ticket the caller can see, ignoring filters. */
  overall: z.number().int(),
  hiddenConfidential: z.number().int(),
  people: z.record(z.string(), personRefSchema),
})
export type BoardResponse = z.infer<typeof boardResponseSchema>

export interface BoardTicketLike {
  id: number
  statusId: string
}

export type BoardLane<T extends BoardTicketLike> =
  | { kind: 'status'; id: string; status: Status; tickets: T[]; total: number }
  /** Tickets whose status was archived (or is unknown): read-only, can't be dropped into. */
  | { kind: 'other'; id: typeof OTHER_LANE_ID; tickets: T[]; total: number }

export const OTHER_LANE_ID = 'other'
export const OTHER_LANE_LABEL = 'Other (archived status)'

/**
 * Board lanes: one per active status in config order (empty ones too), then a single trailing
 * "Other" lane gathering tickets in archived or unknown statuses, only when there are any.
 */
export function boardLanes<T extends BoardTicketLike>(
  statuses: readonly Status[],
  columns: readonly { statusId: string; total: number; tickets: T[] }[],
): BoardLane<T>[] {
  const byStatus = new Map(columns.map((c) => [c.statusId, c]))
  const lanes: BoardLane<T>[] = []
  const activeIds = new Set<string>()
  for (const s of statuses) {
    if (s.archived) continue
    activeIds.add(s.id)
    const col = byStatus.get(s.id)
    lanes.push({
      kind: 'status',
      id: s.id,
      status: s,
      tickets: col?.tickets ?? [],
      total: col?.total ?? 0,
    })
  }
  const rest = columns.filter((c) => !activeIds.has(c.statusId) && c.total > 0)
  if (rest.length > 0) {
    // Archived statuses in config order, unknown ids last.
    const order = (id: string) => {
      const i = statuses.findIndex((s) => s.id === id)
      return i < 0 ? Number.MAX_SAFE_INTEGER : i
    }
    rest.sort((a, b) => order(a.statusId) - order(b.statusId))
    lanes.push({
      kind: 'other',
      id: OTHER_LANE_ID,
      tickets: rest.flatMap((c) => c.tickets),
      total: rest.reduce((n, c) => n + c.total, 0),
    })
  }
  return lanes
}

/**
 * Optimistic board move: the ticket leaves its column and goes to the top of `toStatusId`'s
 * (created when missing), with both totals adjusted. Returns the input unchanged when the ticket
 * isn't on the board or is already there.
 */
export function moveBoardTicket<T extends BoardTicketLike>(
  columns: readonly { statusId: string; total: number; tickets: T[] }[],
  ticketId: number,
  toStatusId: string,
): { statusId: string; total: number; tickets: T[] }[] {
  const from = columns.find((c) => c.tickets.some((t) => t.id === ticketId))
  const ticket = from?.tickets.find((t) => t.id === ticketId)
  if (!from || !ticket || from.statusId === toStatusId) return [...columns]
  const moved = { ...ticket, statusId: toStatusId }
  const out = columns.map((c) => {
    if (c === from)
      return { ...c, total: c.total - 1, tickets: c.tickets.filter((t) => t.id !== ticketId) }
    if (c.statusId === toStatusId)
      return { ...c, total: c.total + 1, tickets: [moved, ...c.tickets] }
    return c
  })
  if (!out.some((c) => c.statusId === toStatusId))
    out.push({ statusId: toStatusId, total: 1, tickets: [moved] })
  return out
}
